/**
 * Associative-array (AcDbAssocArray) typed sub-schemas — Batch-8
 * S-ARRAYASSOC-MODEL (L3).
 *
 * Extracted into an adjacent module (barrel-imported by model.ts) to keep the
 * 2000-line model.ts under the file-size guardrail while still extending
 * `arrayRectEntitySchema` to the full ТЗ §9.4 set as TYPED OBJECT-GRAPHS
 * (CLAUDE.md ПРИОРИТЕТ #1 — never flat fields / booleans).
 *
 * ── §9.4 SCALE resolution (authoritative) ──
 * The per-item scale is NOT a standalone scalar. An associative array's item
 * placement (position + rotation + scale) is the EVALUATED output of the
 * AcDbAssocArray action body. We model that evaluated output as
 * `itemTransforms[]` — a list of affine 3×4 (row-major) matrices, one per
 * generated item — under the associative graph. The entity-level INSERT scale
 * scalars (DXF 41/42/43) round-trip ONLY for the non-associative INSERT-grid
 * fallback (the existing `blockInsertEntitySchema` path), never as the carrier
 * of associative item scale.
 */
import { z } from "zod";

/**
 * A single affine item transform — the evaluated placement of one generated
 * array item (position + rotation + scale baked into the matrix). Stored as a
 * 3×4 row-major matrix (12 reals): rows [m00 m01 m02 m03] / [m10 …] / [m20 …],
 * where the last column is the translation. This is the §9.4 SCALE answer: item
 * scale lives inside the matrix, not as a separate scalar.
 */
export const arrayItemTransformSchema = z.object({
  /** Index of the item within the array grid (0-based, row-major). */
  itemIndex: z.number().int().min(0),
  /**
   * 3×4 row-major affine matrix (12 reals). Encodes the item's position,
   * rotation and scale as the evaluated action-body output.
   */
  matrix: z.array(z.number()).length(12),
});
export type ArrayItemTransform = z.infer<typeof arrayItemTransformSchema>;

/**
 * Maps a generated item index to the source entity it instantiates. AutoCAD
 * arrays can carry multiple source entities; each generated item references one.
 */
export const arrayItemSourceMappingSchema = z.object({
  /** Index of the generated item (row-major). */
  itemIndex: z.number().int().min(0),
  /** Model id (= DXF handle) of the source entity this item instantiates. */
  sourceEntityId: z.string(),
});
export type ArrayItemSourceMapping = z.infer<
  typeof arrayItemSourceMappingSchema
>;

/**
 * Per-item property override. An associative array lets the user override
 * individual items (e.g. erase, replace, or re-property a single copy) while
 * keeping the rest associative. Modeled as an object-graph — NOT a flat flag —
 * per ПРИОРИТЕТ #1.
 */
export const arrayItemOverrideSchema = z.object({
  /** Index of the overridden item (row-major). */
  itemIndex: z.number().int().min(0),
  /** Optional explicit transform override for this item. */
  transform: arrayItemTransformSchema.optional(),
  /**
   * Opaque round-trip payload for override data we decode only verbatim
   * (preserves AutoCAD's per-item override record without loss). Keyed by the
   * source group code.
   */
  rawData: z.record(z.string(), z.string()).optional(),
});
export type ArrayItemOverride = z.infer<typeof arrayItemOverrideSchema>;

/**
 * A replaced item — the user swapped the source content of one item for a
 * different source entity (ARRAYEDIT → "Replace Item").
 */
export const arrayReplacedItemSchema = z.object({
  itemIndex: z.number().int().min(0),
  /** Model id of the replacement source entity. */
  replacementEntityId: z.string(),
});
export type ArrayReplacedItem = z.infer<typeof arrayReplacedItemSchema>;

/**
 * The 3D grid locator of one PERSISTED associative-array item — audit §12
 * S-ARRAYRECT-ITEMS-DECODE. Mirrors the AcDbAssocArrayItem record's
 * OdDbItemLocator (three group-90 int32s — ODA
 * `OdDbImpAssocArrayItem::dxfInFields`, Teigha
 * DbConstraints/DbImpAssocArrayParameters.cpp): `itemIndex` is the COLUMN
 * index for the rectangular family (matches the "Items"=column-count
 * value-param naming), `rowIndex` the row, `levelIndex` the Z-stack level.
 * Kept as the verified 3D locator — NEVER flattened to a single row-major
 * index (the flattening order is a renderer/evaluator concern and deriving
 * it from counts would violate preserve-source-never-infer).
 */
export const arrayItemLocatorSchema = z.object({
  /** Column index (rectangular family) — group 90 #2 on the item record. */
  itemIndex: z.number().int(),
  /** Row index — group 90 #3. */
  rowIndex: z.number().int(),
  /** Level (Z-stack) index — group 90 #4. */
  levelIndex: z.number().int(),
});
export type ArrayItemLocator = z.infer<typeof arrayItemLocatorSchema>;

/**
 * One PERSISTED AcDbAssocArrayItem record projected onto the model — audit
 * §12 S-ARRAYRECT-ITEMS-DECODE. Every field is the verbatim decoded source
 * value (dxf-io `AssocArrayItem` — full grammar + flag-bit reference there);
 * nothing is derived or default-materialized:
 *
 *   - `flags` keeps the raw bit word (kErased=1, kRealtiveTransform=2 [ODA's
 *     own spelling], kDefaulTransform=4, kItemPresent=8, kModified=16,
 *     kChanged=32);
 *   - `erased` / `modified` are the decoded reader-rule booleans
 *     (`erased = kErased | !kItemPresent` — ODA dxfInFields);
 *   - the default transform keeps its SOURCE FORM: translation-only items
 *     persist a `position` point (groups 11/21/31), transformed items a full
 *     4×4 row-major `defaultTransform` (16×40) — mutually exclusive, never
 *     converted into each other;
 *   - `relativeTransform` is the per-item OVERRIDE matrix (a user-moved /
 *     -rotated item's delta, compounded at evaluation — ODA
 *     `getTransform(bCompounded)`); this is the "Item overrides[]" carrier
 *     and where per-item ROTATION lives (the #303 itemRotation research
 *     deferral — no scalar field is ever derived from it);
 *   - `entityProviderHandle` is the item's display block-reference inside the
 *     array's anonymous *U block (the ONLY persisted per-item entity link —
 *     see the `itemSourceMapping` deferral note in dxfBridge.import.ts);
 *   - `repBodyHandle` FKs the AcDbAssocArrayModifyActionBody that
 *     source-replaces this item (ARRAYEDIT → Replace-Item).
 *
 * RENDER FOLLOW-UP (NOT this slice — Cat B backend round-trip): the renderer
 * COULD skip `erased` copies and apply `relativeTransform` per generated
 * copy — that visual arm is the DEFUSE S7 per-item-transform adapter work
 * tracked with the other deferred Cat-C/render arms
 * (docs/superpowers/plans/2026-06-06-fullstack-gap-closure.md ARRAYEDIT-FLOW
 * / DEFUSE S7).
 */
export const arrayAssocItemSchema = z.object({
  locator: arrayItemLocatorSchema,
  /** Verbatim flags word (group 90 #5 on the item record). */
  flags: z.number().int(),
  /** Decoded erased state (kErased | !kItemPresent) — ARRAYEDIT item erase. */
  erased: z.boolean(),
  /** Decoded kModified — the item is source-replaced via `repBodyHandle`. */
  modified: z.boolean(),
  /** Translation-only default transform (11/21/31) — absent when the full matrix form was persisted. */
  position: z
    .object({ x: z.number(), y: z.number(), z: z.number() })
    .optional(),
  /** Full default-transform matrix (4×4 row-major, 16 reals) — kDefaulTransform form. */
  defaultTransform: z.array(z.number()).length(16).optional(),
  /** Per-item relative transform OVERRIDE (4×4 row-major, 16 reals) — kRealtiveTransform. */
  relativeTransform: z.array(z.number()).length(16).optional(),
  /** FK to the item's display block-reference (absent exactly when erased). */
  entityProviderHandle: z.string().optional(),
  /** FK to the overriding AcDbAssocArrayModifyActionBody (present when modified). */
  repBodyHandle: z.string().optional(),
});
export type ArrayAssocItem = z.infer<typeof arrayAssocItemSchema>;

/**
 * Handle linkage to the AcDbAssocArray OBJECTS graph. All optional + round-trip
 * only: the bridge populates these from the resolver so the writer can keep the
 * entity ↔ action-body ↔ params ↔ dependency wiring intact. Opaque to model
 * logic (the geometry is driven by the typed params + itemTransforms above).
 */
export const assocArrayHandlesSchema = z.object({
  /** Handle of the ACDBASSOCARRAYACTIONBODY record. */
  actionBodyHandle: z.string().optional(),
  /** Handle of the ACDBASSOCARRAYRECTANGULARPARAMETERS record. */
  arrayParametersHandle: z.string().optional(),
  /** Handles of the AcDbAssocDependency records (source-entity FKs). */
  dependencyHandles: z.array(z.string()).default([]),
  /**
   * AW-1 — handle of the owning ACDBASSOCACTION record. The action (NOT the
   * action body) carries the named value params ("Items"/"Rows"/"AxesAngle"/
   * … — dxf-io `RECT_NAMED_VALUE_PARAMS`) the write-back encoder
   * (`dxfBridge.arrayAssocExport.ts` / dxf-io `assocArrayPatch.ts`)
   * surgically patches. Round-trip-only linkage, mirrors `actionBodyHandle`.
   */
  actionHandle: z.string().optional(),
  /**
   * @codex P2 ("Track the owning action per named field") — per-field
   * ACDBASSOCACTION owner handle (keyed by the §9.4 field name — columnCount/
   * rowCount/levelCount/columnSpacing/rowSpacing/levelSpacing/axisAngle/
   * rowElevation), mirrored verbatim from dxf-io's
   * `ResolvedAssocArray.ownerHandleByField` (handleResolution.ts). Present
   * ONLY when the graph's named value params are genuinely SPLIT across more
   * than one ACDBASSOCACTION — the common single-action case leaves this
   * absent and write-back falls back to the single `actionHandle` above for
   * every field. The export bridge (`dxfBridge.arrayAssocExport.ts`) threads
   * this straight into dxf-io's `AssocArrayGraphPatchTarget.ownerHandleByField`
   * so a split array's edits land on the record that genuinely owns each
   * field.
   */
  ownerHandleByField: z.record(z.string(), z.string()).optional(),
});
export type AssocArrayHandles = z.infer<typeof assocArrayHandlesSchema>;

/**
 * AW-0 — the AW-1 "8 fields" write-back matrix (docs/MASTERPLAN.md AW.2):
 * columnCount/rowCount/levelCount/columnSpacing/rowSpacing/levelSpacing/
 * axisAngle/rowElevation, every one absence-preserving optional. ONE domain
 * truth shared by `assocArrayParamsSnapshotSchema` (the entity-level
 * dirty-diff comparison surface) and `assocArrayCarrierRectParamsSchema`
 * (the decoded graph carrier's named fields, which additionally carries the
 * positional fallback arrays) — `dup:check` flagged the two field lists as
 * an exact clone before this extraction.
 */
const assocArrayRectFieldsSchema = z.object({
  columnCount: z.number().optional(),
  rowCount: z.number().optional(),
  levelCount: z.number().optional(),
  columnSpacing: z.number().optional(),
  rowSpacing: z.number().optional(),
  levelSpacing: z.number().optional(),
  axisAngle: z.number().optional(),
  rowElevation: z.number().optional(),
});

/**
 * AW-0 — the §9.4 named-field SURFACE the DXF bridge decided at import time
 * for an associative array, captured as a companion snapshot right after
 * `applyAssocArrayResolution` writes those same 8 fields onto the entity
 * (dxfBridge.import.ts). Round-trip-only / opaque to model logic (same
 * status as `assocArrayHandlesSchema` above) — it exists SOLELY so the export
 * bridge's by-construction dirty-diff choke point
 * (dxfBridge.arrayAssocExport.ts) can tell "unchanged since import" from "a
 * write path mutated the entity's typed fields after import" WITHOUT
 * re-running the resolver's ACDBASSOCACTION named-value-param /
 * standalone-record merge a second time at export (a single computation,
 * snapshotted once, can never drift from what import actually decided).
 *
 * Present only on an entity `applyAssocArrayResolution` actually ran for
 * (i.e. `associative: true` with a resolved graph) — a non-associative
 * INSERT-grid array has no graph to diverge from (its fields persist
 * directly via entity groups 70/71/44/45, already byte-stable) and never
 * gets one.
 *
 * Comparison surface = exactly the fields `applyAssocArrayResolution`
 * conditionally writes (columnCount/rowCount/levelCount/columnSpacing/
 * rowSpacing/levelSpacing/axisAngle/rowElevation) — the AW-1 "8 fields"
 * write-back matrix (docs/MASTERPLAN.md AW.2). Deliberately NOT the raw
 * `AssocArrayRectParams` positional arrays / expressions — those are not
 * part of the entity's own typed-field comparison surface.
 *
 * A field absent here (the graph never supplied it) is never compared —
 * `undefined` never reads as "dirty" against a defaulted entity value
 * (preserve-source-never-infer: absence is not a claim).
 */
export const assocArrayParamsSnapshotSchema = assocArrayRectFieldsSchema;
export type AssocArrayParamsSnapshot = z.infer<
  typeof assocArrayParamsSnapshotSchema
>;

/** Shared `{code, value}` DXF group-pair shape for the carrier schemas below. */
const dxfGroupPairSchema = z.object({
  code: z.number().int(),
  value: z.string(),
});

/**
 * AW-0 — typed mirror of dxf-io's `AssocArrayRectParams` (decoded rectangular
 * parameter snapshot: named fields + the positional int/double/expression
 * fallback arrays a producer may have used instead). Part of the
 * `assocArrayCarrierObjectSchema` graph carrier below.
 */
export const assocArrayCarrierRectParamsSchema = assocArrayRectFieldsSchema.extend({
  intParams: z.array(z.number()),
  doubleParams: z.array(z.number()),
  expressions: z.array(z.string()),
});
export type AssocArrayCarrierRectParams = z.infer<
  typeof assocArrayCarrierRectParamsSchema
>;

/**
 * AW-0 — typed mirror of dxf-io's `AssocActionValueParam` (a single named
 * ValueParam decoded from an ACDBASSOCACTION record — the REAL AutoCAD
 * carrier of the associative-array counts/spacings; see
 * `handleResolution.ts` grammar reference). Part of the graph carrier below.
 */
export const assocArrayCarrierValueParamSchema = z.object({
  name: z.string(),
  unitType: z.number().optional(),
  values: z.array(
    z.object({ code: z.number().int(), value: z.number() }),
  ),
});
export type AssocArrayCarrierValueParam = z.infer<
  typeof assocArrayCarrierValueParamSchema
>;

/**
 * AW-0 — typed mirror of dxf-io's `AssocArrayItem` (one inline
 * AcDbAssocArrayItem record). Reuses `arrayAssocItemSchema` field-for-field
 * (the MODEL-facing resolved projection already matches the dxf-io type
 * exactly) plus the one field the model projection deliberately drops —
 * `rawGroups`, the item's own verbatim group slice — since the CARRIER needs
 * it for byte-stable re-emission (the model's `assocItems` is the semantic
 * view; this is the transport view).
 */
export const assocArrayCarrierItemSchema = arrayAssocItemSchema.extend({
  rawGroups: z.array(dxfGroupPairSchema),
});
export type AssocArrayCarrierItem = z.infer<
  typeof assocArrayCarrierItemSchema
>;

/**
 * AW-0 foundation — typed mirror of dxf-io's `DxfAssocArrayObject`: one
 * decoded AcDbAssoc* OBJECTS record (ACDBASSOCNETWORK / ACDBASSOCACTION /
 * ACDBASSOCARRAYACTIONBODY / ACDBASSOCARRAYRECTANGULARPARAMETERS /
 * ACDBASSOCDEPENDENCY / …). This is the typed carrier docs/MASTERPLAN.md
 * AW-0 threads through the DrawingDocument (`rawAssocArrayObjects`) INSTEAD
 * of flattening the graph into the opaque `rawObjectGroups` catch-all: every
 * record keeps its OWN verbatim `rawGroups` (the byte-stable round-trip
 * channel `assocArrayWriter.ts#serializeAssocArrayObject` already re-emits
 * verbatim when populated — UNCHANGED by this slice) while ALSO exposing its
 * decoded fields so a later slice (AW-1+) can locate + surgically patch a
 * specific named ValueParam without re-parsing an opaque byte stream.
 *
 * Field-for-field mirror of `packages/dxf-io/src/types.ts`'s
 * `DxfAssocArrayObject` (kept in `drawing-model` rather than imported as a
 * zod schema from `dxf-io` because dxf-io does not depend on zod — see that
 * type's own doc comment for the full per-field grammar references).
 */
export const assocArrayCarrierObjectSchema = z.object({
  /** Self handle (group 5). */
  handle: z.string(),
  /** Owner handle (first 330 at control depth 0). */
  ownerHandle: z.string().optional(),
  /** Concrete object type (group 0) — e.g. "ACDBASSOCARRAYACTIONBODY". */
  objectType: z.string(),
  /** Ordered list of 100-code subclass markers (round-trip + dispatch). */
  subclassMarkers: z.array(z.string()),
  /** Parameter family this record carries (rectangular fully decoded). */
  paramFamily: z.enum(["rectangular", "polar", "path", "modify", "none"]),
  /** Decoded rectangular parameters — present only for the rect-params record. */
  rectParams: assocArrayCarrierRectParamsSchema.optional(),
  /** Embedded parameters CLASS NAME (action-body inline params object). */
  paramsClassName: z.string().optional(),
  /** Item-list CLASS NAME (action-body inline item records). */
  itemsClassName: z.string().optional(),
  /** Inline per-item AcDbAssocArrayItem records. */
  items: z.array(assocArrayCarrierItemSchema).optional(),
  /** ACDBASSOCARRAYMODIFYACTIONBODY — locators of the items it overrides. */
  overriddenItemLocators: z.array(arrayItemLocatorSchema).optional(),
  /** ACDBASSOCACTION — named value parameters (the real AutoCAD counts/spacings). */
  valueParams: z.array(assocArrayCarrierValueParamSchema).optional(),
  /** ACDBASSOCDEPENDENCY — handle of the referenced (source-entity) object. */
  dependencyRefHandle: z.string().optional(),
  /** Full raw group list for verbatim re-emission via the writer. */
  rawGroups: z.array(dxfGroupPairSchema),
});
export type AssocArrayCarrierObject = z.infer<
  typeof assocArrayCarrierObjectSchema
>;
